import { useState, useEffect } from "react";

const WhatsAppChatWidget = ({
  phoneNumber = "+919962114477",
  welcomeMessage = "Hey! Have a question? Drop us a message.",
  companyName = "VishuShethra Industries",
  buttonText = "Any questions? Ask in Whatsapp",
  defaultQuestions = [
    "I need help with your product features",
    "I'd like to know about pricing options",
  ],
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [isMobile, setIsMobile] = useState(false);

  // Check if device is mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const toggleChat = () => {
    setIsOpen(!isOpen);
  };

  const selectQuestion = (questionText) => {
    setSelectedQuestion({
      id: Date.now(),
      text: questionText,
    });
  };

  const startWhatsAppChat = () => {
    const messageToSend =
      selectedQuestion?.text || "Hello! I have a question about your products.";
    const encodedMessage = encodeURIComponent(messageToSend);
    window.open(
      `https://wa.me/${phoneNumber}?text=${encodedMessage}`,
      "_blank"
    );
  };

  const getCurrentTime = () => {
    return new Date().toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getCurrentDate = () => {
    return new Date().toLocaleDateString("en-GB");
  };

  // Styles
  const styles = {
    container: {
      position: "fixed",
      bottom: "24px",
      right: "24px",
      zIndex: 50,
    },
    chatButton: {
      display: "flex",
      alignItems: "center",
      padding: isMobile ? "12px" : "12px 16px",
      backgroundColor: "#22c55e",
      color: "white",
      borderRadius: isMobile ? "50%" : "50px",
      boxShadow:
        "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
      border: "none",
      cursor: "pointer",
      transition: "all 0.3s ease",
      fontSize: "14px",
      fontWeight: "500",
      width: isMobile ? "60px" : "auto",
      height: isMobile ? "60px" : "auto",
      justifyContent: isMobile ? "center" : "flex-start",
    },
    chatButtonHover: {
      backgroundColor: "#16a34a",
      transform: "scale(1.05)",
    },
    chatWidget: {
      width: isMobile ? "90vw" : "320px",
      maxWidth: isMobile ? "350px" : "320px",
      borderRadius: "8px",
      boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
      overflow: "hidden",
      animation: "slideInFromBottom 0.3s ease-out",
      right: isMobile ? "5vw" : "0",
      bottom: isMobile ? "80px" : "0",
      position: isMobile ? "fixed" : "relative",
    },
    header: {
      backgroundColor: "#134e4a",
      color: "white",
      padding: "12px",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
    },
    headerLeft: {
      display: "flex",
      alignItems: "center",
    },
    backButton: {
      padding: "4px",
      marginRight: "8px",
      backgroundColor: "transparent",
      border: "none",
      color: "white",
      cursor: "pointer",
      borderRadius: "4px",
      transition: "background-color 0.2s",
    },
    backButtonHover: {
      backgroundColor: "#0f766e",
    },
    avatar: {
      width: "40px",
      height: "40px",
      borderRadius: "50%",
      backgroundColor: "white",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      marginRight: "12px",
    },
    avatarText: {
      color: "#134e4a",
      fontWeight: "bold",
      fontSize: "18px",
    },
    headerInfo: {
      display: "flex",
      flexDirection: "column",
    },
    companyName: {
      fontWeight: "600",
      fontSize: "14px",
      margin: 0,
    },
    lastSeen: {
      fontSize: "12px",
      opacity: 0.8,
      margin: 0,
    },
    closeButton: {
      padding: "8px",
      backgroundColor: "transparent",
      border: "none",
      color: "white",
      cursor: "pointer",
      borderRadius: "4px",
      transition: "background-color 0.2s",
    },
    closeButtonHover: {
      backgroundColor: "#0f766e",
    },
    chatContent: {
      padding: isMobile ? "12px" : "16px",
      height: isMobile ? "280px" : "320px",
      overflowY: "auto",
      backgroundColor: "#e5ddd5",
      backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100' viewBox='0 0 100 100'%3E%3Cpath fill='%23d1ccc0' fill-opacity='0.4' d='M11 18c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm48 25c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm-43-7c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm63 31c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM34 90c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm56-76c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM12 86c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm28-65c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm23-11c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-6 60c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm29 22c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zM32 63c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm57-13c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-9-21c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM60 91c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM35 41c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM12 60c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2z'/%3E%3C/svg%3E")`,
      backgroundSize: "200px",
    },
    dateIndicator: {
      display: "flex",
      justifyContent: "center",
      marginBottom: "16px",
    },
    dateChip: {
      backgroundColor: "rgba(255, 255, 255, 0.9)",
      color: "#6b7280",
      fontSize: "12px",
      padding: "4px 12px",
      borderRadius: "50px",
      boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.1)",
    },
    messageContainer: {
      display: "flex",
      marginBottom: "16px",
    },
    messageContainerEnd: {
      display: "flex",
      justifyContent: "flex-end",
      marginBottom: "16px",
      marginTop: "16px",
    },
    messageWrapper: {
      maxWidth: "75%",
    },
    messageBubble: {
      backgroundColor: "white",
      borderRadius: "8px",
      padding: "12px",
      boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.1)",
      border: "1px solid #e5e7eb",
    },
    messageBubbleUser: {
      backgroundColor: "#dcfce7",
      borderRadius: "8px",
      padding: "12px",
      boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.1)",
    },
    messageText: {
      color: "#374151",
      fontSize: "14px",
      margin: 0,
      lineHeight: "1.4",
    },
    messageTextUser: {
      color: "#1f2937",
      fontSize: "14px",
      margin: 0,
      lineHeight: "1.4",
    },
    messageTime: {
      color: "#9ca3af",
      fontSize: "12px",
      textAlign: "right",
      marginTop: "8px",
      display: "flex",
      justifyContent: "flex-end",
      alignItems: "center",
    },
    messageTimeUser: {
      color: "#6b7280",
      fontSize: "12px",
      textAlign: "right",
      marginTop: "8px",
      display: "flex",
      justifyContent: "flex-end",
      alignItems: "center",
    },
    messageProfile: {
      display: "flex",
      alignItems: "center",
      marginTop: "4px",
      paddingLeft: "8px",
    },
    profileAvatar: {
      width: "24px",
      height: "24px",
      borderRadius: "50%",
      backgroundColor: "white",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      border: "1px solid #e5e7eb",
    },
    profileAvatarText: {
      color: "#22c55e",
      fontWeight: "bold",
      fontSize: "12px",
    },
    profileName: {
      fontSize: "12px",
      color: "#6b7280",
      marginLeft: "8px",
    },
    quickReplies: {
      display: "flex",
      flexDirection: "column",
      gap: "8px",
      marginTop: "16px",
    },
    quickReplyButton: {
      backgroundColor: "white",
      textAlign: "left",
      width: "100%",
      borderRadius: "8px",
      padding: "12px",
      boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.1)",
      border: "1px solid #e5e7eb",
      cursor: "pointer",
      fontSize: "14px",
      transition: "all 0.2s ease",
    },
    quickReplyButtonHover: {
      backgroundColor: "#f9fafb",
      boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
    },
    quickReplyText: {
      color: "#374151",
    },
    footer: {
      backgroundColor: "#f9fafb",
      padding: isMobile ? "12px" : "16px",
      borderTop: "1px solid #e5e7eb",
    },
    startChatButton: {
      width: "100%",
      backgroundColor: "#22c55e",
      color: "white",
      padding: "12px 16px",
      borderRadius: "8px",
      border: "none",
      cursor: "pointer",
      transition: "all 0.2s ease",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "8px",
      fontWeight: "500",
      fontSize: "14px",
    },
    startChatButtonHover: {
      backgroundColor: "#16a34a",
      boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1)",
      transform: "scale(1.02)",
    },
    icon: {
      width: "20px",
      height: "20px",
      fill: "currentColor",
    },
    iconSmall: {
      width: "16px",
      height: "16px",
      fill: "currentColor",
    },
  };

  // Icon Components
  const WhatsAppIcon = () => (
    <svg
      style={styles.icon}
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.570-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.890-5.335 11.893-11.893A11.821 11.821 0 0020.46 3.488z" />
    </svg>
  );

  const SendIcon = () => (
    <svg
      style={styles.icon}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
      />
    </svg>
  );

  const BackArrowIcon = () => (
    <svg
      style={styles.icon}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M10 19l-7-7m0 0l7-7m-7 7h18"
      />
    </svg>
  );

  const CloseIcon = () => (
    <svg
      style={{ ...styles.icon, width: "24px", height: "24px" }}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2"
        d="M6 18L18 6M6 6l12 12"
      />
    </svg>
  );

  const DoubleCheckIcon = ({ isBlue = false }) => (
    <svg
      style={{
        ...styles.iconSmall,
        marginLeft: "4px",
        color: isBlue ? "#3b82f6" : "#9ca3af",
      }}
      viewBox="0 0 16 15"
      fill="currentColor"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M15.01 3.316l-.478-.372a.365.365 0 0 0-.51.063L8.666 9.879a.32.32 0 0 1-.484.033l-.358-.325a.319.319 0 0 0-.484.032l-.378.483a.418.418 0 0 0 .036.541l1.32 1.266c.143.14.361.125.484-.033l6.272-8.048a.366.366 0 0 0-.064-.512zm-4.1 0l-.478-.372a.365.365 0 0 0-.51.063L4.566 9.879a.32.32 0 0 1-.484.033L1.891 7.769a.366.366 0 0 0-.515.006l-.423.433a.364.364 0 0 0 .006.514l3.258 3.185c.143.14.361.125.484-.033l6.272-8.048a.365.365 0 0 0-.063-.51z" />
    </svg>
  );

  return (
    <>
      <style>
        {`
          @keyframes slideInFromBottom {
            from {
              transform: translateY(100%);
              opacity: 0;
            }
            to {
              transform: translateY(0);
              opacity: 1;
            }
          }
        `}
      </style>
      <div style={styles.container}>
        {/* Chat Button */}
        {!isOpen && (
          <button
            style={styles.chatButton}
            onClick={toggleChat}
            onMouseEnter={(e) => {
              Object.assign(e.target.style, styles.chatButtonHover);
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = "#22c55e";
              e.target.style.transform = "scale(1)";
            }}
          >
            <WhatsAppIcon />
            {!isMobile && (
              <span style={{ marginLeft: "8px" }}>{buttonText}</span>
            )}
          </button>
        )}

        {/* Chat Widget */}
        {isOpen && (
          <div style={styles.chatWidget}>
            {/* Header */}
            <div style={styles.header}>
              <div style={styles.headerLeft}>
                <button
                  style={styles.backButton}
                  onMouseEnter={(e) => {
                    Object.assign(e.target.style, styles.backButtonHover);
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = "transparent";
                  }}
                >
                  <BackArrowIcon />
                </button>
                <div style={styles.avatar}>
                  <div style={styles.avatarText}>{companyName.charAt(0)}</div>
                </div>
                <div style={styles.headerInfo}>
                  <h3 style={styles.companyName}>{companyName}</h3>
                  <p style={styles.lastSeen}>
                    last seen today at {getCurrentTime()}
                  </p>
                </div>
              </div>
              <button
                style={styles.closeButton}
                onClick={toggleChat}
                onMouseEnter={(e) => {
                  Object.assign(e.target.style, styles.closeButtonHover);
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = "transparent";
                }}
              >
                <CloseIcon />
              </button>
            </div>

            {/* Chat Content */}
            <div style={styles.chatContent}>
              {/* Date Indicator */}
              <div style={styles.dateIndicator}>
                <span style={styles.dateChip}>{getCurrentDate()}</span>
              </div>

              {/* Welcome Message */}
              <div style={styles.messageContainer}>
                <div style={styles.messageWrapper}>
                  <div style={styles.messageBubble}>
                    <div style={styles.messageText}>{welcomeMessage}</div>
                    <div style={styles.messageTime}>
                      <span>{getCurrentTime()}</span>
                      <DoubleCheckIcon />
                    </div>
                  </div>
                  <div style={styles.messageProfile}>
                    <div style={styles.profileAvatar}>
                      <div style={styles.profileAvatarText}>
                        {companyName.charAt(0)}
                      </div>
                    </div>
                    <span style={styles.profileName}>{companyName}</span>
                  </div>
                </div>
              </div>

              {/* Quick Reply Buttons */}
              {!selectedQuestion && (
                <div style={styles.quickReplies}>
                  {defaultQuestions.map((question, index) => (
                    <button
                      key={index}
                      style={styles.quickReplyButton}
                      onClick={() => selectQuestion(question)}
                      onMouseEnter={(e) => {
                        Object.assign(
                          e.target.style,
                          styles.quickReplyButtonHover
                        );
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = "white";
                        e.target.style.boxShadow =
                          "0 1px 3px 0 rgba(0, 0, 0, 0.1)";
                      }}
                    >
                      <span style={styles.quickReplyText}>{question}</span>
                    </button>
                  ))}
                </div>
              )}

              {/* Selected Question (User message) */}
              {selectedQuestion && (
                <div style={styles.messageContainerEnd}>
                  <div style={styles.messageWrapper}>
                    <div style={styles.messageBubbleUser}>
                      <div style={styles.messageTextUser}>
                        {selectedQuestion.text}
                      </div>
                      <div style={styles.messageTimeUser}>
                        <span>{getCurrentTime()}</span>
                        <DoubleCheckIcon isBlue={true} />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Response after question selection */}
              {selectedQuestion && (
                <div style={styles.messageContainer}>
                  <div style={styles.messageWrapper}>
                    <div style={styles.messageBubble}>
                      <div style={styles.messageText}>
                        Thanks for your question! Let's continue this
                        conversation on WhatsApp.
                      </div>
                      <div style={styles.messageTime}>
                        <span>{getCurrentTime()}</span>
                        <DoubleCheckIcon />
                      </div>
                    </div>
                    <div style={styles.messageProfile}>
                      <div style={styles.profileAvatar}>
                        <div style={styles.profileAvatarText}>
                          {companyName.charAt(0)}
                        </div>
                      </div>
                      <span style={styles.profileName}>{companyName}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div style={styles.footer}>
              <button
                style={styles.startChatButton}
                onClick={startWhatsAppChat}
                onMouseEnter={(e) => {
                  Object.assign(e.target.style, styles.startChatButtonHover);
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = "#22c55e";
                  e.target.style.boxShadow = "none";
                  e.target.style.transform = "scale(1)";
                }}
              >
                <SendIcon />
                <span>Start Chat on WhatsApp</span>
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default WhatsAppChatWidget;
